import { AggregationType, AxisSettings, GlobalSettings } from '@twaice-fe/shared/models';
import { DataPointDE, Sensor } from './data-explorer-types';

export interface DataExplorerQueryParams {
  componentBk: string[];
  sk: string[];
  gs: string;
  as: string[];
  startDate: string;
  endDate: string;
  timeRes: string;
  timeRange: string;
}

export function getAxisAggregationType(sensor: Sensor, axis: AxisSettings[], globalSettings: GlobalSettings): AggregationType {
  if (sensor.valueType === 'position') {
    return axis.find((axisItem) => axisItem.name.includes('Position'))?.aggregationType || AggregationType.AVG;
  }
  if (globalSettings.groupBy === 'unit') {
    return axis.find((axisItem) => axisItem.unit === sensor.unit)?.aggregationType || AggregationType.AVG;
  } else {
    return axis.find((axisItem) => axisItem.key === sensor.key)?.aggregationType || AggregationType.AVG;
  }
}

export function mapSensorsByAggregation<T extends Sensor>(
  sensors: T[],
  axis: AxisSettings[],
  globalSettings: GlobalSettings
): Array<{ aggregationType: AggregationType; sensors: T[] }> {
  const mappedSensors = sensors.reduce(
    (acc, sensor: T) => {
      const aggregationType = getAxisAggregationType(sensor, axis, globalSettings);
      if (!acc[aggregationType]) {
        acc[aggregationType] = [];
      }
      acc[aggregationType].push(sensor);
      return acc;
    },
    {} as Record<AggregationType, T[]>
  );

  return Object.entries(mappedSensors).map(([aggregationType, sensorsList]) => ({
    aggregationType: aggregationType as AggregationType,
    sensors: sensorsList,
  }));
}

export function appendDataPoints<T extends DataPointDE>(stateData: T[] | null, dataPoints: T[]): T[] {
  if (!stateData) {
    return dataPoints;
  }

  const existingDataMap = new Map<string, T>();
  stateData.forEach((dp) => {
    const key = `${dp.componentBk}_${dp.sensor.key}`;
    existingDataMap.set(key, dp);
  });

  const updatedData = dataPoints.map((newDp) => {
    const key = `${newDp.componentBk}_${newDp.sensor.key}`;

    if (existingDataMap.has(key)) {
      const existingDp = existingDataMap.get(key) || undefined;
      const uniqueMap = new Map();
      [...newDp.data, ...existingDp.data].forEach((point) => {
        uniqueMap.set(point[0], point);
      });
      const mergedData = Array.from(uniqueMap.values()).sort((a, b) => new Date(a[0]).getTime() - new Date(b[0]).getTime());

      return {
        ...newDp,
        data: mergedData,
      };
    }

    return { ...newDp };
  });

  const missingData = stateData.filter(
    (dp) => !updatedData.some((newDp) => `${newDp.componentBk}_${newDp.sensor.key}` === `${dp.componentBk}_${dp.sensor.key}`)
  );

  return [...updatedData, ...missingData];
}
